import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { RootState } from '../../store'
import clsx from 'clsx'
import { formatDistanceToNow, format, startOfDay, endOfDay, isWithinInterval } from 'date-fns'

interface TestSession {
  id: number
  session_id: string
  assessment: {
    id: number
    title: string
    assessment_type: string
    duration_minutes: number
  }
  status: string
  duration_minutes: number
  created_at: string
  completed_at?: string
  driveTitle?: string
}

const TestHistoryPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)
  
  const [testHistory, setTestHistory] = useState<TestSession[]>([])
  const [filteredTestHistory, setFilteredTestHistory] = useState<TestSession[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [dateFilter, setDateFilter] = useState<{
    startDate: string
    endDate: string
  }>({
    startDate: '',
    endDate: ''
  })
  const [dateValidationError, setDateValidationError] = useState<string>('')

  useEffect(() => {
    loadTestHistory()
  }, [])

  useEffect(() => {
    applyDateFilter()
  }, [testHistory, dateFilter])

  const loadTestHistory = async () => {
    try {
      setIsLoading(true)
      
      // Get candidate's email from auth state
      const candidateEmail = user?.email
      if (!candidateEmail) {
        setTestHistory([])
        return
      }

      // API is the single source of truth - using relative URL for IIS proxy
      try {
        const thUrl = `/api/v1/candidate/test-history?candidate_email=${encodeURIComponent(candidateEmail)}`
        const thRes = await fetch(thUrl, { headers: { 'Cache-Control': 'no-cache' } })
        if (thRes.ok) {
          const apiSessions = await thRes.json()
          setTestHistory((apiSessions || []) as any)
        } else {
          setTestHistory([])
        }
      } catch (e) {
        setTestHistory([])
      }
      
    } catch (error) {
      console.error('Failed to load test history:', error)
      setTestHistory([])
    } finally {
      setIsLoading(false)
    }
  }

  const applyDateFilter = () => {
    // If no filters are applied, show all tests
    if (!dateFilter.startDate && !dateFilter.endDate) {
      setFilteredTestHistory(testHistory)
      return
    }

    // Validate date range before filtering
    if (dateFilter.startDate && dateFilter.endDate) {
      const startDate = new Date(dateFilter.startDate)
      const endDate = new Date(dateFilter.endDate)
      
      // If end date is before start date, don't filter (show all tests)
      if (endDate < startDate) {
        setFilteredTestHistory(testHistory)
        return
      }
    }

    const filtered = testHistory.filter((test) => {
      try {
        const testDate = new Date(test.completed_at || test.created_at)
        
        // Check if testDate is valid
        if (isNaN(testDate.getTime())) {
          return false
        }
        
        if (dateFilter.startDate && dateFilter.endDate) {
          const startDate = startOfDay(new Date(dateFilter.startDate))
          const endDate = endOfDay(new Date(dateFilter.endDate))
          return isWithinInterval(testDate, { start: startDate, end: endDate })
        } else if (dateFilter.startDate) {
          const startDate = startOfDay(new Date(dateFilter.startDate))
          return testDate >= startDate
        } else if (dateFilter.endDate) {
          const endDate = endOfDay(new Date(dateFilter.endDate))
          return testDate <= endDate
        }
        
        return true
      } catch (error) {
        console.error('Error filtering test date:', error)
        return false
      }
    })

    setFilteredTestHistory(filtered)
  }

  const handleDateFilterChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateFilter(prev => {
      const newFilter = {
        ...prev,
        [field]: value
      }

      // Clear any previous validation errors
      setDateValidationError('')

      // Validate that the date is not in the future
      const selectedDate = new Date(value)
      const today = new Date()
      today.setHours(23, 59, 59, 999) // End of today

      if (selectedDate > today) {
        setDateValidationError('Cannot select future dates')
        return prev // Don't update the filter
      }

      // Validate date range
      if (newFilter.startDate && newFilter.endDate) {
        const startDate = new Date(newFilter.startDate)
        const endDate = new Date(newFilter.endDate)
        
        // If end date is before start date, clear the end date and show error
        if (endDate < startDate) {
          newFilter.endDate = ''
          setDateValidationError('End date must be after start date')
        }
      }

      return newFilter
    })
  }

  const clearDateFilter = () => {
    setDateFilter({
      startDate: '',
      endDate: ''
    })
    setDateValidationError('')
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800' },
      started: { color: 'bg-blue-100 text-blue-800' },
      expired: { color: 'bg-red-100 text-red-800' },
      cancelled: { color: 'bg-gray-100 text-gray-800' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.cancelled
    
    return (
      <span className={clsx('inline-flex items-center px-2 py-1 rounded text-xs font-medium', config.color)}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }


  const getAssessmentTypeIcon = (type: string) => {
    return null
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading test history...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </button>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Test History
              </h1>
              <p className="text-gray-600 mt-1">
                View all your completed assessments and track your progress
              </p>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        {testHistory.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Total Tests</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{testHistory.length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test History List */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">All Tests</h2>
                <p className="text-gray-600 text-sm mt-1">
                  Complete history of your assessments
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Filter by date</span>
              </div>
            </div>
            
            {/* Date Filter */}
            <div className={`flex items-center space-x-4 p-4 rounded-lg ${
              dateFilter.startDate || dateFilter.endDate 
                ? 'bg-blue-50 border border-blue-200' 
                : 'bg-gray-50'
            }`}>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">From:</label>
                <input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                  max={dateFilter.endDate || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">To:</label>
                <input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                  min={dateFilter.startDate || undefined}
                  max={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={clearDateFilter}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Clear
              </button>
            </div>
            
            {/* Date Validation Error */}
            {dateValidationError && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{dateValidationError}</p>
              </div>
            )}
          </div>
          
          {filteredTestHistory.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {testHistory.length === 0 ? 'No Tests Found' : 'No Tests in Date Range'}
              </h3>
              <p className="text-gray-500 mb-4">
                {testHistory.length === 0 
                  ? "You haven't completed any assessments yet"
                  : "Try adjusting your date filter to see more results"
                }
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredTestHistory.map((test) => (
                <div key={test.session_id || (test as any).id || Math.random()} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {(test as any).assessment_title || test.assessment?.title || 'Assessment'}
                        </h3>
                        {test.driveTitle && (
                          <p className="text-sm text-gray-500">
                            From: {test.driveTitle}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 mb-3">
                        {getStatusBadge(test.status)}
                      </div>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div>
                          <span>
                            Completed: {format(new Date(test.completed_at || test.created_at), 'MMM dd, yyyy')}
                          </span>
                        </div>
                        <div>
                          <span>
                            Duration: {test.duration_minutes ?? 0} minutes
                          </span>
                        </div>
                        <div className="text-gray-500">
                          {formatDistanceToNow(new Date(test.completed_at || test.created_at), { addSuffix: true })}
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TestHistoryPage
